import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '../../../context/ProjectContext';
import { useToast } from '../../../context/ToastContext';
import { useAuth } from '../../../context/AuthContext';
import { supabase } from '../../../lib/supabase';
import { ArrowLeftIcon, CheckCircleIcon, ExclamationCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { runSEOCheck } from '../../../components/agents/seoChecker';

export default function WebsiteContent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentProject } = useProject();
  const { addToast } = useToast();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [document, setDocument] = useState(null);
  const [seoResults, setSeoResults] = useState(null);
  const [formValues, setFormValues] = useState({
    url: '',
    keyword: '',
    title: ''
  });

  useEffect(() => {
    loadDocument();
  }, [id]);

  useEffect(() => {
    if (document) {
      setFormValues({
        url: document.url || '',
        keyword: document.keyword || '',
        title: document.title || ''
      });
    }
  }, [document]);

  const loadDocument = async () => {
    try {
      const { data, error } = await supabase
        .from('website_seo_checks')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setDocument(data);
      
      if (data.seo_results) {
        setSeoResults(data.seo_results);
      }
    } catch (error) {
      console.error('Error loading document:', error);
      addToast('Error loading document', 'error');
      navigate('/content/documents');
    } finally {
      setLoading(false);
    }
  };

  const analyzeSEO = async (url, keyword) => {
    try {
      setAnalyzing(true);
      
      const response = await fetch(`https://aai.bigg.co.uk/webhook/scrapehtml`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url })
      });

      if (!response.ok) throw new Error('Failed to fetch website content');

      console.log(response);
      
      const html = await response.text();

      console.log(html);
      
      const results = runSEOCheck(html, url);
      setSeoResults(results);

      const { error } = await supabase
        .from('website_seo_checks')
        .update({
          seo_results: results,
          updated_at: new Date().toISOString(),
          updated_by: user.id
        })
        .eq('id', id);

      if (error) throw error;

    } catch (error) {
      console.error('Error analyzing SEO:', error);
      addToast('Failed to analyze website', 'error');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleUrlChange = (e) => {
    setFormValues(prev => ({ ...prev, url: e.target.value }));
  };

  const handleKeywordChange = (e) => {
    setFormValues(prev => ({ ...prev, keyword: e.target.value }));
  };

  const handleUrlBlur = async () => {
    if (formValues.url === document.url) return; // Don't save if unchanged
    
    try {
      const { error } = await supabase
        .from('website_seo_checks')
        .update({ 
          url: formValues.url,
          updated_at: new Date().toISOString(),
          updated_by: user.id
        })
        .eq('id', id);
      
      if (error) throw error;
      setDocument(prev => ({ ...prev, url: formValues.url }));
    } catch (error) {
      addToast('Failed to update URL', 'error');
    }
  };

  const handleKeywordBlur = async () => {
    if (formValues.keyword === document.keyword) return; // Don't save if unchanged
    
    try {
      const { error } = await supabase
        .from('website_seo_checks')
        .update({ 
          keyword: formValues.keyword,
          updated_at: new Date().toISOString(),
          updated_by: user.id
        })
        .eq('id', id);
      
      if (error) throw error;
      setDocument(prev => ({ ...prev, keyword: formValues.keyword }));
    } catch (error) {
      addToast('Failed to update keyword', 'error');
    }
  };

  const handleTitleChange = async (e) => {
    const newTitle = e.target.value;
    setFormValues(prev => ({ ...prev, title: newTitle }));
  };

  const handleTitleBlur = async () => {
    if (formValues.title === document.title) return; // Don't save if unchanged
    
    try {
      const { error } = await supabase
        .from('website_seo_checks')
        .update({ 
          title: formValues.title,
          updated_at: new Date().toISOString(),
          updated_by: user.id
        })
        .eq('id', id);
      
      if (error) throw error;
      setDocument(prev => ({ ...prev, title: formValues.title }));
    } catch (error) {
      addToast('Failed to update title', 'error');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getScoreIcon = (score) => {
    if (score >= 80) return <CheckCircleIcon className="w-6 h-6 text-green-500" />;
    if (score >= 60) return <ExclamationCircleIcon className="w-6 h-6 text-yellow-500" />;
    return <XCircleIcon className="w-6 h-6 text-red-500" />;
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/content/documents')}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          <input
            type="text"
            value={formValues.title}
            onChange={handleTitleChange}
            onBlur={handleTitleBlur}
            className="text-2xl font-bold bg-transparent hover:bg-dark-300 px-2 py-1 rounded focus:outline-none focus:bg-dark-300 focus:ring-1 focus:ring-accent-primary"
            placeholder="Enter title..."
          />
        </div>
      </div>

      <div className="bg-dark-200 rounded-lg p-6">
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Website URL</label>
            <input
              type="url"
              value={formValues.url}
              onChange={handleUrlChange}
              onBlur={handleUrlBlur}
              className="w-full bg-dark-300 border border-divider rounded-lg px-4 py-2"
              placeholder="https://example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Target Keyword</label>
            <input
              type="text"
              value={formValues.keyword}
              onChange={handleKeywordChange}
              onBlur={handleKeywordBlur}
              className="w-full bg-dark-300 border border-divider rounded-lg px-4 py-2"
              placeholder="Main keyword to analyze"
            />
          </div>
        </div>

        <button
          onClick={() => analyzeSEO(formValues.url, formValues.keyword)}
          disabled={analyzing || !formValues.url || !formValues.keyword}
          className="px-4 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {analyzing ? 'Analyzing...' : 'Analyze SEO'}
        </button>

        {seoResults && (
          <div className="mt-8 space-y-6">
            {/* Overall Score Card */}
            <div className="bg-dark-300 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Overall SEO Score</h2>
                <div className="flex items-center gap-2">
                  {getScoreIcon(seoResults.overall_score)}
                  <span className={`text-3xl font-bold ${getScoreColor(seoResults.overall_score)}`}>
                    {seoResults.overall_score}%
                  </span>
                </div>
              </div>
            </div>

            {/* Score Categories Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {seoResults.checks.map((check, index) => (
                <div key={index} className="bg-dark-300 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium">{check.topic}</h3>
                    <div className="flex items-center gap-2">
                      {getScoreIcon(check.score)}
                      <span className={`text-lg font-semibold ${getScoreColor(check.score)}`}>
                        {check.score}%
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-400">{check.commentary}</p>
                </div>
              ))}
            </div>

            {/* Group Suggestions by Category */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Critical Issues */}
              {seoResults.checks.some(check => check.score < 60) && (
                <div className="bg-dark-300 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-red-500 flex items-center gap-2">
                    <XCircleIcon className="w-5 h-5" />
                    Critical Issues
                  </h3>
                  <ul className="space-y-3">
                    {seoResults.checks
                      .filter(check => check.score < 60)
                      .map((check, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-300">{check.commentary}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}

              {/* Improvements Needed */}
              {seoResults.checks.some(check => check.score >= 60 && check.score < 80) && (
                <div className="bg-dark-300 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-yellow-500 flex items-center gap-2">
                    <ExclamationCircleIcon className="w-5 h-5" />
                    Improvements Needed
                  </h3>
                  <ul className="space-y-3">
                    {seoResults.checks
                      .filter(check => check.score >= 60 && check.score < 80)
                      .map((check, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <ExclamationCircleIcon className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-300">{check.commentary}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}

              {/* Passed Checks */}
              {seoResults.checks.some(check => check.score >= 80) && (
                <div className="bg-dark-300 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-green-500 flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5" />
                    Passed Checks
                  </h3>
                  <ul className="space-y-3">
                    {seoResults.checks
                      .filter(check => check.score >= 80)
                      .map((check, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-300">{check.commentary}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 